export const WIZARD_PATH = '/store/drops/wizard'

export const WIZARD_STEPS = [
  { key: 'what', label: 'Drop' },
  { key: 'often', label: 'How often' },
  { key: 'day', label: 'Made on' },
  { key: 'get', label: 'Customers get it' },
  { key: 'close', label: 'Ordering' },
  { key: 'who', label: 'Who' },
  { key: 'pay', label: 'Payment' },
  { key: 'review', label: 'Review' }
]

export const visibleSteps = () => WIZARD_STEPS.map((step, index) => ({ ...step, index }))
export const questionSteps = () => visibleSteps().filter(step => step.key !== 'review')
export const stepForKey = key => WIZARD_STEPS.find(step => step.key === key)

export const RHYTHMS = [
  { value: 'weekly', title: 'Every week', subtitle: 'The same day or days each week.', many: true },
  { value: 'biweekly', title: 'Every other week', subtitle: 'One day, alternating weeks.', many: false },
  {
    value: 'four_weekly',
    title: 'Every 4 weeks',
    subtitle:
      'One day, every fourth week. The date moves with the weeks, so it is not always the same date of the month.',
    many: false
  },
  { value: 'once', title: 'Just once', subtitle: 'A single date, like a holiday special.', many: false }
]

export const WEEKDAYS = [
  { value: 1, label: 'Mon', long: 'Monday' },
  { value: 2, label: 'Tue', long: 'Tuesday' },
  { value: 3, label: 'Wed', long: 'Wednesday' },
  { value: 4, label: 'Thu', long: 'Thursday' },
  { value: 5, label: 'Fri', long: 'Friday' },
  { value: 6, label: 'Sat', long: 'Saturday' },
  { value: 0, label: 'Sun', long: 'Sunday' }
]

export const WINDOW_PRESETS = [
  { value: 'morning', name: 'Morning', start: '08:00', end: '12:00' },
  { value: 'midday', name: 'Midday', start: '11:00', end: '15:00' },
  { value: 'afternoon', name: 'Afternoon', start: '12:00', end: '17:00' },
  { value: 'allday', name: 'All day', start: '08:00', end: '17:00' },
  { value: 'evening', name: 'Evening', start: '16:00', end: '20:00' },
  { value: 'custom', name: 'Custom', start: null, end: null }
]

export const CUTOFF_PRESETS = [
  {
    value: 'morning',
    title: 'The morning of, 6:00 AM',
    subtitle: 'Last-minute orders right up to production.',
    daysBefore: 0,
    time: '06:00'
  },
  {
    value: 'daybefore',
    title: 'The day before, 8:00 PM',
    subtitle: 'The most common choice. Gives you a night to plan.',
    daysBefore: 1,
    time: '20:00'
  },
  {
    value: 'twodays',
    title: 'Two days before, 12:00 PM',
    subtitle: 'Time to order ingredients before you start.',
    daysBefore: 2,
    time: '12:00'
  },
  {
    value: 'custom',
    title: 'Custom',
    subtitle: 'Pick how many days before and at what time.',
    daysBefore: null,
    time: null
  }
]

export const OPENS_OPTIONS = [
  { value: 'previous', name: 'When the last one closes' },
  { value: 'week', name: 'A week before', days: 7 },
  { value: 'three', name: '3 days before', days: 3 }
]

export const ONCE_OPENS_OPTIONS = [
  { value: 'now', name: 'Now' },
  { value: 'week', name: 'A week before', days: 7 },
  { value: 'three', name: '3 days before', days: 3 }
]

export const REMINDER_WHENS = [
  { value: 'opens', name: 'When ordering opens' },
  { value: 'day_before_close', name: 'The day before ordering closes' },
  { value: 'before_close', name: 'A few hours before ordering closes' },
  { value: 'custom', name: 'Custom time' }
]

export const AUDIENCE_KINDS = [
  { value: 'everyone', title: 'Everyone', subtitle: 'Anyone on your contacts list.' },
  { value: 'tiers', title: 'Members of a membership', subtitle: 'Customers on the memberships you pick.' },
  { value: 'lists', title: 'A list', subtitle: 'Only people on the lists you pick.' }
]

export const HOURS = Array.from({ length: 17 }, (_, i) => {
  const hour = i + 6
  const twelve = hour % 12 || 12
  return { value: `${String(hour).padStart(2, '0')}:00`, name: `${twelve}:00 ${hour < 12 ? 'AM' : 'PM'}` }
})

export const CUSTOM_DAYS_MAX = 14
export const CLOSE_DAYS_MAX = 7

let reminderSeq = 0

export const newReminder = when => ({
  id: `r${Date.now()}-${(reminderSeq += 1)}`,
  when,
  daysBefore: 2,
  time: '10:00',
  body: null,
  attachVcard: false
})

export const defaultWizardData = () => ({
  name: '',
  storeId: null,
  rhythm: null,
  weekdays: [],
  date: null,
  fulfillment: { methodId: null, newMethod: null },
  arrival: { offsetDays: 0, window: 'allday', start: '08:00', end: '17:00' },
  ordering: {
    cutoff: 'daybefore',
    closesDaysBefore: 1,
    closesTime: '20:00',
    opens: 'previous',
    limitOn: false,
    limit: 40
  },
  audience: { kind: null, tierAudienceIds: [], lists: [] },
  reminders: [newReminder('opens'), newReminder('before_close')],
  payment: { autocharge: null, autofill: false, autofillKind: 'refill', autofillTip: false, cartItems: [] }
})

export const newMethodDraft = () => ({
  type: 'pickup',
  name: '',
  address: { address1: '', address2: '', city: '', state: '', zip: '' },
  zone: { kind: 'zips', zipCodes: [], states: [], waypoints: [] }
})

// copy

export const dropLabel = data => data.name.trim() || 'this drop'

const HEADINGS = {
  what: () => 'What are you selling?',
  often: name => `How often do you make ${name}?`,
  day: name => `Which day do you make ${name}?`,
  get: name => `How do customers get ${name}?`,
  close: name => `When should ordering close for ${name}?`,
  who: name => `Who can order ${name}, and should we remind them?`,
  pay: name => `How should customers pay for ${name}?`,
  review: () => 'Does this look right?'
}

export const stepHeading = (key, data) => HEADINGS[key](dropLabel(data))

const SUBTITLES = {
  often: () => 'Bottle keeps six months of dates ahead and adds more as time passes.',
  close: () => 'Customers can order until the cutoff. If you autocharge, that happens at the same moment.',
  who: () =>
    'Reminders go by text to people on the list who have not ordered yet. Customers who already ordered, skipped, or left the list are not texted.',
  review: () => "Everything here can be changed from the drop's page afterwards."
}

export const stepSubtitle = (key, data) => (SUBTITLES[key] ? SUBTITLES[key](dropLabel(data)) : '')

const SECTIONS = {
  details: () => ({ title: 'Name and store', description: 'Customers see this name on your store and in reminders.' }),
  rhythm: () => ({
    title: 'Rhythm',
    description: 'Bottle keeps six months of dates ahead and adds more as time passes.'
  }),
  days: name => ({
    title: 'Production day',
    description: `The day you make ${name}. Everything else is set relative to it.`
  }),
  date: name => ({ title: 'Date', description: `The one day you make ${name}.` }),
  method: name => ({
    title: 'Fulfillment method',
    description: `How customers receive ${name}. Pick one you already have, or set up a new one.`
  }),
  arrival: name => ({
    title: 'When it arrives',
    description: `When customers get ${name}, counted from the day you make it.`
  }),
  closes: () => ({
    title: 'Ordering closes',
    description: 'The cutoff, counted from the production day. With several days, from the first one.'
  }),
  opens: () => ({ title: 'Ordering opens', description: 'When customers can start ordering for the next date.' }),
  limit: () => ({
    title: 'Order limit',
    description: 'The most orders you take for each date. Ordering closes early once it is reached.'
  }),
  audience: name => ({
    title: 'Who can order',
    description: `Everyone on the list gets a cart for ${name} and can order while it is open.`
  }),
  reminders: () => ({
    title: 'Text reminders',
    description: 'Add as many as you like. Your order link is added to the end of every text.'
  }),
  payment: () => ({ title: 'Payment', description: 'How carts turn into paid orders when ordering closes.' }),
  carts: () => ({ title: 'Carts', description: 'What is in the cart when the reminder arrives.' })
}

export const sectionCopy = (key, data) => SECTIONS[key](dropLabel(data))

// reminders

const DEFAULT_BODIES = {
  opens: name => `Hi {{first_name}}! ${name} is open for ordering. Grab yours before it closes.`,
  before_close: name => `Last chance, {{first_name}}: ordering for ${name} closes in a few hours.`,
  day_before_close: name => `Hi {{first_name}}, ordering for ${name} closes tomorrow.`,
  custom: name => `Hi {{first_name}}! ${name} is open for ordering.`
}

export const reminderBody = (reminder, data) =>
  reminder.body === null ? DEFAULT_BODIES[reminder.when](data.name.trim() || 'the drop') : reminder.body

export const nextReminderWhen = data => {
  const used = new Set(data.reminders.map(reminder => reminder.when))
  return REMINDER_WHENS.find(option => option.value !== 'custom' && !used.has(option.value))?.value ?? 'custom'
}

// rhythm changes

export const rhythmAllowsManyDays = rhythm => RHYTHMS.find(option => option.value === rhythm)?.many === true

export const normalizeForRhythm = data => {
  if (data.rhythm === 'once') {
    data.weekdays = []
    if (data.ordering.opens === 'previous') data.ordering.opens = 'now'
    // A one-off can open now, when an "ordering opens" text would never send.
    data.reminders.forEach(reminder => {
      if (reminder.when === 'opens' && reminder.body === null) reminder.when = 'day_before_close'
    })
    return data
  }
  data.date = null
  if (data.ordering.opens === 'now') data.ordering.opens = 'previous'
  if (!rhythmAllowsManyDays(data.rhythm) && data.weekdays.length > 1) data.weekdays = [data.weekdays[0]]
  return data
}

// validity

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/

const newMethodValid = method => {
  if (!method || !['pickup', 'delivery'].includes(method.type) || !method.name.trim()) return false
  if (method.type === 'pickup') return ['address1', 'city', 'state', 'zip'].every(field => method.address[field].trim())
  return zoneValid(method.zone)
}

export const MIN_AREA_POINTS = 3

export const ZONE_KINDS = [
  { value: 'zips', name: 'These zip codes' },
  { value: 'states', name: 'These states' },
  { value: 'map', name: 'This area' },
  { value: 'anywhere', name: 'Anywhere' }
]

const zoneValid = zone => {
  if (zone.kind === 'anywhere') return true
  if (zone.kind === 'zips') return zone.zipCodes.length > 0
  if (zone.kind === 'states') return zone.states.length > 0
  if (zone.kind === 'map') return zone.waypoints.length >= MIN_AREA_POINTS
  return false
}

const windowValid = arrival => {
  if (arrival.window !== 'custom') return true
  return TIME.test(arrival.start) && TIME.test(arrival.end) && arrival.end > arrival.start
}

const audienceValid = audience =>
  audience.kind === 'everyone' ||
  (audience.kind === 'tiers' && audience.tierAudienceIds.length > 0) ||
  (audience.kind === 'lists' && audience.lists.length > 0)

const reminderValid = (reminder, data) =>
  REMINDER_WHENS.some(option => option.value === reminder.when) &&
  reminderBody(reminder, data).trim().length > 0 &&
  (reminder.when !== 'custom' ||
    (Number.isInteger(reminder.daysBefore) &&
      reminder.daysBefore >= 0 &&
      reminder.daysBefore <= CUSTOM_DAYS_MAX &&
      TIME.test(reminder.time)))

export const AUTOFILL_KINDS = [
  {
    value: 'refill',
    title: 'Their last order',
    subtitle:
      "Each customer's cart is refilled with what they ordered last time, as long as those products are still on the menu."
  },
  {
    value: 'category',
    title: 'The same amount from each category',
    subtitle:
      "Keeps each customer's usual counts per category, say five lunches and two snacks, filled with this week's products. Good for a rotating menu."
  },
  { value: 'cart', title: 'A cart you build', subtitle: 'Every customer starts with the same items. Pick them below.' }
]

const autofillValid = payment => {
  if (!payment.autofill) return true
  if (!AUTOFILL_KINDS.some(kind => kind.value === payment.autofillKind)) return false
  if (payment.autofillKind !== 'cart') return true
  return (
    payment.cartItems.length > 0 &&
    payment.cartItems.every(item => Number.isInteger(item.quantity) && item.quantity >= 1)
  )
}

const VALIDATORS = {
  what: data => Boolean(data.name.trim()) && Boolean(data.storeId),
  often: data => RHYTHMS.some(option => option.value === data.rhythm),
  day: data => {
    if (data.rhythm === 'once') return Boolean(data.date)
    if (!data.weekdays.length) return false
    return rhythmAllowsManyDays(data.rhythm) || data.weekdays.length === 1
  },
  get: data =>
    (Boolean(data.fulfillment.methodId) || newMethodValid(data.fulfillment.newMethod)) && windowValid(data.arrival),
  close: data => {
    const { ordering } = data
    if (
      ordering.cutoff === 'custom' &&
      !(
        Number.isInteger(ordering.closesDaysBefore) &&
        ordering.closesDaysBefore >= 0 &&
        ordering.closesDaysBefore <= CLOSE_DAYS_MAX &&
        TIME.test(ordering.closesTime)
      )
    )
      return false
    return !ordering.limitOn || (Number.isInteger(ordering.limit) && ordering.limit > 0)
  },
  who: data => audienceValid(data.audience) && data.reminders.every(reminder => reminderValid(reminder, data)),
  pay: data => typeof data.payment.autocharge === 'boolean' && autofillValid(data.payment),
  review: () => true
}

export const stepValid = (key, data) => VALIDATORS[key](data)
export const allStepsValid = data => questionSteps().every(step => stepValid(step.key, data))
